import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { DollarSign, Users, MousePointerClick, TrendingUp, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all analytics data
      const [statsRes, dashRes, leadsRes, clicksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/stats`),
        axios.get(`${BACKEND_URL}/api/analytics/dashboard`),
        axios.get(`${BACKEND_URL}/api/leads?limit=50`),
        axios.get(`${BACKEND_URL}/api/clicks?limit=50`)
      ]);

      setStats(statsRes.data);
      setDashboardData(dashRes.data);
      setLeads(leadsRes.data);
      setClicks(clicksRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your money tracker...</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const totalPotentialEarnings = clicks.reduce((sum, click) => sum + click.commission_amount, 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Your Money Dashboard</h1>
            <p className="text-gray-600">Track every lead and every potential dollar</p>
          </div>
          <Button onClick={fetchData} className="bg-emerald-600 hover:bg-emerald-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          {/* Total Leads */}
          <Card className="p-6 bg-white shadow-lg border-l-4 border-blue-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm font-semibold text-blue-600">Total Leads</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.total_leads || 0}</div>
            <p className="text-sm text-gray-600 mt-2">
              Today: <strong>{dashboardData?.leads?.today || 0}</strong>
            </p>
          </Card>

          {/* Total Clicks */}
          <Card className="p-6 bg-white shadow-lg border-l-4 border-purple-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <MousePointerClick className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm font-semibold text-purple-600">Total Clicks</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.total_clicks || 0}</div>
            <p className="text-sm text-gray-600 mt-2">
              Today: <strong>{dashboardData?.clicks?.today || 0}</strong>
            </p>
          </Card>

          {/* Conversion Rate */}
          <Card className="p-6 bg-white shadow-lg border-l-4 border-amber-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
              <span className="text-sm font-semibold text-amber-600">Conversion</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.conversion_rate || 0}%</div>
            <p className="text-sm text-gray-600 mt-2">Clicks per Lead</p>
          </Card>

          {/* Potential Earnings */}
          <Card className="p-6 bg-white shadow-lg border-l-4 border-emerald-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <span className="text-sm font-semibold text-emerald-600">Potential $</span>
            </div>
            <div className="text-3xl font-bold text-emerald-600">
              ${totalPotentialEarnings.toFixed(2)}
            </div>
            <p className="text-sm text-gray-600 mt-2">If all clicks convert</p>
          </Card>
        </div>

        {/* Weekly Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
            <h3 className="font-semibold text-gray-900 mb-4">This Week</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Leads:</span>
                <strong className="text-gray-900">{dashboardData?.leads?.week || 0}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Clicks:</span>
                <strong className="text-gray-900">{dashboardData?.clicks?.week || 0}</strong>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100">
            <h3 className="font-semibold text-gray-900 mb-4">This Month</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Leads:</span>
                <strong className="text-gray-900">{dashboardData?.leads?.month || 0}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Clicks:</span>
                <strong className="text-gray-900">{dashboardData?.clicks?.month || 0}</strong>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100">
            <h3 className="font-semibold text-gray-900 mb-4">Potential This Month</h3>
            <div className="text-3xl font-bold text-emerald-600 mb-2">
              ${dashboardData?.potential_earnings?.month?.toFixed(2) || '0.00'}
            </div>
            <p className="text-sm text-gray-600">Based on clicks tracked</p>
          </Card>
        </div>

        {/* Top Products */}
        {stats?.top_products && stats.top_products.length > 0 && (
          <Card className="p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">🔥 Top Clicked Products</h3>
            <div className="space-y-3">
              {stats.top_products.map((product, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-semibold text-gray-900">{product.product_name}</div>
                    <div className="text-sm text-gray-600">{product.clicks} clicks</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-emerald-600">
                      ${product.potential_earnings.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-600">potential</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Recent Activity */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Leads */}
          <Card className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📧 Recent Email Captures</h3>
            {leads.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No leads captured yet. Start promoting!</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {leads.map((lead, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900">{lead.name}</span>
                      <span className="text-xs text-gray-600 bg-blue-100 px-2 py-1 rounded">
                        {lead.source}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">{lead.email}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDate(lead.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent Clicks */}
          <Card className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">🎯 Recent Affiliate Clicks</h3>
            {clicks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No clicks yet. Keep promoting!</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {clicks.map((click, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900 text-sm">
                        {click.product_name}
                      </span>
                      <span className="text-emerald-600 font-bold">
                        ${click.commission_amount}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">{click.affiliate_network}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDate(click.clicked_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Important Note */}
        <Card className="p-6 mt-8 bg-amber-50 border-2 border-amber-200">
          <h3 className="text-lg font-bold text-amber-900 mb-3">💰 How You Actually Get Paid</h3>
          <div className="space-y-2 text-sm text-amber-900">
            <p>
              <strong>Clicks tracked here</strong> = potential commissions. This shows what COULD earn if people buy.
            </p>
            <p>
              <strong>Actual money</strong> appears in your affiliate network accounts (ClickBank, ShareASale, etc.) when people complete purchases.
            </p>
            <p>
              <strong>Check your affiliate dashboards:</strong> That's where you'll see real confirmed sales and commissions.
            </p>
            <p className="pt-2 border-t border-amber-300">
              <strong>This dashboard tracks:</strong> Leads captured + Clicks = Your marketing performance
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
export default Dashboard;
